import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowBackIos,
  Search,
  KeyboardArrowDown,
  CheckCircleOutline,
  HighlightOff,
  AddCircleOutline,
  EditOutlined,
  DeleteOutlined,
  ChevronLeft,
  ChevronRight,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  Close,
  InfoOutlined,
  Check,
  CheckCircle
} from "@mui/icons-material";
import { PidAnalysisService } from "modules/processing-hub/data/services/pid-analysis.service";
import {
  CreatePidAnalysisRecordRequest,
  PidAnalysisRecord,
  PidAnalysisReviewSummary
} from "modules/processing-hub/data/models/pid-analysis.model";
import { mapKnowledgeGraphResponse } from "modules/processing-hub/data/utils/knowledge-graph.util";
import GraphCanvas from "modules/knowledge-library/pages/knowledge-library/components/graph-canvas/GraphCanvas";
import {
  GraphEdge,
  GraphNode
} from "modules/knowledge-library/data/models/knowledge-graph.model";
import "./RelationshipValidation.scss";

type RelationshipStatus = "Reviewed" | "Pending";
type SourceTag = "AI" | "AI + Edited" | "Manual";

interface RelationshipRow {
  id: string;
  sourceEquipment: string;
  sourceType: string;
  relationship: string;
  relationshipTone: "process" | "signal" | "thermal";
  description: string;
  destinationType: string;
  destinationEquipment: string;
  status: RelationshipStatus;
  source: SourceTag;
}

const getRelationshipTone = (
  connectionType: string
): RelationshipRow["relationshipTone"] => {
  const value = connectionType.toLowerCase();
  if (value.includes("signal")) return "signal";
  if (value.includes("thermal")) return "thermal";
  return "process";
};

const mapRecordToRow = (record: PidAnalysisRecord): RelationshipRow => ({
  id: record.analysisId,
  sourceEquipment: record.sourceAsset,
  sourceType: record.sourceAssetType,
  relationship: (record.connectionType || "").toUpperCase(),
  relationshipTone: getRelationshipTone(record.connectionType || ""),
  description: record.lineStreamDescription,
  destinationType: record.destinationAssetType,
  destinationEquipment: record.destinationAsset,
  status:
    (record.status || "").toUpperCase() === "REVIEWED" ? "Reviewed" : "Pending",
  source: record.source as SourceTag
});

const EMPTY_FORM: CreatePidAnalysisRecordRequest = {
  sourceAsset: "",
  sourceAssetType: "",
  connectionType: "",
  lineStreamDescription: "",
  destinationAsset: "",
  destinationAssetType: ""
};

const RelationshipValidation: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [searchTerm, setSearchTerm] = useState("");
  const [relationshipFilter, setRelationshipFilter] = useState("");
  const [sourceTypeFilter, setSourceTypeFilter] = useState("");
  const [openFilter, setOpenFilter] = useState<
    "relationship" | "sourceType" | null
  >(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [editRow, setEditRow] = useState<RelationshipRow | null>(null);
  const [drawerMode, setDrawerMode] = useState<"edit" | "add" | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [rows, setRows] = useState<RelationshipRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formValues, setFormValues] =
    useState<CreatePidAnalysisRecordRequest>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteRow, setDeleteRow] = useState<RelationshipRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [reviewSummary, setReviewSummary] =
    useState<PidAnalysisReviewSummary | null>(null);
  const [isGeneratingGraph, setIsGeneratingGraph] = useState(false);
  const [generateGraphError, setGenerateGraphError] = useState<string | null>(
    null
  ); // Backend ignores `source`, so track add/edit overrides on the client.
  const [sourceOverrides, setSourceOverrides] = useState<
    Record<string, SourceTag>
  >({});
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewNodes, setPreviewNodes] = useState<GraphNode[]>([]);
  const [previewEdges, setPreviewEdges] = useState<GraphEdge[]>([]);
  const [previewSelectedNodeId, setPreviewSelectedNodeId] = useState<
    string | null
  >(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const documentGuidCode = id;
  const closeDrawer = () => {
    setDrawerMode(null);
    setEditRow(null);
    setFormValues(EMPTY_FORM);
    setSaveError(null);
  };

  useEffect(() => {
    if (!drawerMode) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeDrawer();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [drawerMode]);

  useEffect(() => {
    if (!showReviewModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isGeneratingGraph) {
        setShowReviewModal(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showReviewModal, isGeneratingGraph]);

  useEffect(() => {
    if (!showPreviewModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowPreviewModal(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showPreviewModal]);

  const loadRecords = useCallback(async () => {
    if (!documentGuidCode) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await PidAnalysisService.getPidAnalysis(documentGuidCode);
      if (result.hasErrors) {
        setError(result.errors.join(", ") || "Failed to load relationships.");
        setRows([]);
        return;
      }
      setRows((result.value?.records ?? []).map(mapRecordToRow));
    } catch {
      setError("Failed to load relationships.");
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [documentGuidCode]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleFormChange = (
    field: keyof CreatePidAnalysisRecordRequest,
    value: string
  ) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddRelationship = async () => {
    if (!documentGuidCode) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const result = await PidAnalysisService.addPidAnalysisRecord(
        documentGuidCode,
        formValues
      );
      if (result.hasErrors) {
        setSaveError(result.errors.join(", ") || "Failed to add relationship.");
        return;
      }
      const newId = result.value?.analysisId;
      if (newId) {
        setSourceOverrides((prev) => ({ ...prev, [newId]: "Manual" }));
        setHighlightId(newId);
      }
      closeDrawer();
      await loadRecords();
    } catch {
      setSaveError("Failed to add relationship.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateRelationship = async () => {
    if (!editRow) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const result = await PidAnalysisService.updatePidAnalysisRecord(
        editRow.id,
        formValues
      );
      if (result.hasErrors) {
        setSaveError(
          result.errors.join(", ") || "Failed to update relationship."
        );
        return;
      }
      setSourceOverrides((prev) => ({
        ...prev,
        [editRow.id]: "AI + Edited"
      }));
      setHighlightId(editRow.id);
      closeDrawer();
      await loadRecords();
    } catch {
      setSaveError("Failed to update relationship.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteRow) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const result = await PidAnalysisService.deletePidAnalysisRecords([
        deleteRow.id
      ]);
      if (result.hasErrors) {
        setDeleteError(
          result.errors.join(", ") || "Failed to delete relationship."
        );
        return;
      }
      setDeleteRow(null);
      await loadRecords();
    } catch {
      setDeleteError("Failed to delete relationship.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRejectSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setIsRejecting(true);
    setRejectError(null);
    try {
      const result = await PidAnalysisService.deletePidAnalysisRecords(ids);
      if (result.hasErrors) {
        setRejectError(
          result.errors.join(", ") || "Failed to reject selected relationships."
        );
        return;
      }
      setShowRejectConfirm(false);
      setSelectedIds(new Set());
      await loadRecords();
    } catch {
      setRejectError("Failed to reject selected relationships.");
    } finally {
      setIsRejecting(false);
    }
  };

  const handleAcceptSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setIsAccepting(true);
    setAcceptError(null);
    try {
      const result = await PidAnalysisService.acceptPidAnalysisRecords(ids);
      if (result.hasErrors) {
        setAcceptError(
          result.errors.join(", ") || "Failed to accept selected relationships."
        );
        return;
      }
      setSelectedIds(new Set());
      await loadRecords();
    } catch {
      setAcceptError("Failed to accept selected relationships.");
    } finally {
      setIsAccepting(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!documentGuidCode) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const result = await PidAnalysisService.submitReviewSummary(
        documentGuidCode
      );
      if (result.hasErrors) {
        setSubmitError(result.errors.join(", ") || "Failed to submit review.");
        return;
      }
      setReviewSummary(result.value ?? null);
      setShowReviewModal(true);
    } catch {
      setSubmitError("Failed to submit review.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const drawingId = id || "PRD-102-EXT";

  const handlePreviewGraph = async () => {
    if (!documentGuidCode) return;
    setShowPreviewModal(true);
    setIsLoadingPreview(true);
    setPreviewError(null);
    setPreviewSelectedNodeId(null);
    try {
      const result = await PidAnalysisService.previewKnowledgeGraph(
        documentGuidCode
      );
      if (result.hasErrors) {
        setPreviewError(
          result.errors.join(", ") || "Failed to load knowledge graph preview."
        );
        setPreviewNodes([]);
        setPreviewEdges([]);
        return;
      }
      if (result.value) {
        const mapped = mapKnowledgeGraphResponse(result.value);
        setPreviewNodes(mapped.nodes);
        setPreviewEdges(mapped.edges);
      }
    } catch {
      setPreviewError("Failed to load knowledge graph preview.");
      setPreviewNodes([]);
      setPreviewEdges([]);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleGenerateKnowledgeGraph = async () => {
    if (!documentGuidCode) return;
    setIsGeneratingGraph(true);
    setGenerateGraphError(null);
    try {
      const result = await PidAnalysisService.generateKnowledgeGraph(
        documentGuidCode
      );
      if (result.hasErrors) {
        console.error("Generate Knowledge Graph Error:", result.errors);
        setGenerateGraphError(
          result.errors.join(", ") || "Failed to generate knowledge graph."
        );
        return;
      }
      setShowReviewModal(false);
      navigate(`/drawing-intelligence/${documentGuidCode}`);
    } catch (err: any) {
      console.error("Generate Knowledge Graph Exception:", err);
      const errorMsg =
        err?.response?.data?.errors?.general?.[0] ||
        err?.message ||
        "Failed to generate knowledge graph.";
      setGenerateGraphError(errorMsg);
    } finally {
      setIsGeneratingGraph(false);
    }
  };

  const relationshipOptions = useMemo(
    () =>
      Array.from(
        new Set(rows.map((row) => row.relationship).filter(Boolean))
      ).sort(),
    [rows]
  );

  const sourceTypeOptions = useMemo(
    () =>
      Array.from(
        new Set(rows.map((row) => row.sourceType).filter(Boolean))
      ).sort(),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const withSource = rows.map((row) =>
      sourceOverrides[row.id]
        ? { ...row, source: sourceOverrides[row.id] }
        : row
    );
    const term = searchTerm.trim().toLowerCase();
    return withSource.filter((row) => {
      if (relationshipFilter && row.relationship !== relationshipFilter) {
        return false;
      }
      if (sourceTypeFilter && row.sourceType !== sourceTypeFilter) {
        return false;
      }
      if (!term) return true;
      return [
        row.sourceEquipment,
        row.sourceType,
        row.relationship,
        row.description,
        row.destinationType,
        row.destinationEquipment
      ]
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }, [searchTerm, rows, sourceOverrides, relationshipFilter, sourceTypeFilter]);

  const totalCount = rows.length;
  const reviewedCount = rows.filter((row) => row.status === "Reviewed").length;
  const remainingCount = totalCount - reviewedCount;
  const reviewProgress =
    totalCount > 0 ? Math.round((reviewedCount / totalCount) * 100) : 0;

  const allSelected =
    filteredRows.length > 0 &&
    filteredRows.every((row) => selectedIds.has(row.id));

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (allSelected) return new Set();
      return new Set(filteredRows.map((row) => row.id));
    });
  };

  const toggleRow = (rowId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  return (
    <div className="relationship-validation">
      <button className="rv-back" onClick={() => navigate(-1)}>
        <ArrowBackIos className="rv-back-icon" />
        Back
      </button>

      <h1 className="rv-title">Relationship Validation</h1>
      <p className="rv-subtitle">
        Review and verify automated equipment relationships extracted from
        P&amp;ID drawing {drawingId}.
      </p>

      {/* Stats bar */}
      <div className="rv-stats">
        <div className="rv-stat">
          <span className="rv-stat-label">DRAWING ID</span>
          <span className="rv-stat-value">{drawingId}</span>
        </div>
        <div className="rv-stat">
          <span className="rv-stat-label">AI CONFIDENCE</span>
          <span className="rv-stat-value rv-confidence">
            94%
            <span className="rv-confidence-bar">
              <span className="rv-confidence-fill" style={{ width: "94%" }} />
            </span>
          </span>
        </div>
        <div className="rv-stat">
          <span className="rv-stat-label">RELATIONSHIPS</span>
          <span className="rv-stat-value">{totalCount}</span>
        </div>
        <div className="rv-stat">
          <span className="rv-stat-label">REVIEWED</span>
          <span className="rv-stat-value rv-reviewed">{reviewedCount}</span>
        </div>
        <div className="rv-stat">
          <span className="rv-stat-label">REMAINING</span>
          <span className="rv-stat-value rv-remaining">{remainingCount}</span>
        </div>
        <div className="rv-stat rv-stat-progress">
          <span className="rv-stat-label rv-progress-label">
            Review Progress{" "}
            <span className="rv-progress-pct">{reviewProgress}%</span>
          </span>
          <span className="rv-progress-bar">
            <span
              className="rv-progress-fill"
              style={{ width: `${reviewProgress}%` }}
            />
          </span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="rv-toolbar">
        <div className="rv-search">
          <Search className="rv-search-icon" />
          <input
            type="text"
            placeholder="Search relationships..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className={`rv-filter${openFilter ? " rv-filter-open" : ""}`}>
          <button
            className={`rv-select${
              relationshipFilter ? " rv-select-active" : ""
            }`}
            onClick={() =>
              setOpenFilter((prev) =>
                prev === "relationship" ? null : "relationship"
              )
            }
          >
            {relationshipFilter || "Relationship Type"} <KeyboardArrowDown />
          </button>
          {openFilter === "relationship" && (
            <div className="rv-filter-menu">
              <button
                className="rv-filter-option"
                onClick={() => {
                  setRelationshipFilter("");
                  setOpenFilter(null);
                }}
              >
                All
              </button>
              {relationshipOptions.map((option) => (
                <button
                  key={option}
                  className={`rv-filter-option${
                    relationshipFilter === option
                      ? " rv-filter-option-active"
                      : ""
                  }`}
                  onClick={() => {
                    setRelationshipFilter(option);
                    setOpenFilter(null);
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className={`rv-filter${openFilter ? " rv-filter-open" : ""}`}>
          <button
            className={`rv-select${
              sourceTypeFilter ? " rv-select-active" : ""
            }`}
            onClick={() =>
              setOpenFilter((prev) =>
                prev === "sourceType" ? null : "sourceType"
              )
            }
          >
            {sourceTypeFilter || "Source Type"} <KeyboardArrowDown />
          </button>
          {openFilter === "sourceType" && (
            <div className="rv-filter-menu">
              <button
                className="rv-filter-option"
                onClick={() => {
                  setSourceTypeFilter("");
                  setOpenFilter(null);
                }}
              >
                All
              </button>
              {sourceTypeOptions.map((option) => (
                <button
                  key={option}
                  className={`rv-filter-option${
                    sourceTypeFilter === option
                      ? " rv-filter-option-active"
                      : ""
                  }`}
                  onClick={() => {
                    setSourceTypeFilter(option);
                    setOpenFilter(null);
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>
        {openFilter && (
          <button
            className="rv-filter-backdrop"
            aria-label="Close filter"
            onClick={() => setOpenFilter(null)}
          />
        )}
        <div className="rv-toolbar-actions">
          <button
            className="rv-btn rv-btn-accept"
            disabled={selectedIds.size === 0 || isAccepting}
            onClick={handleAcceptSelected}
          >
            <CheckCircleOutline />{" "}
            {isAccepting ? "Accepting..." : "Accept Selected"}
          </button>
          <button
            className="rv-btn rv-btn-reject"
            disabled={selectedIds.size === 0}
            onClick={() => {
              setRejectError(null);
              setShowRejectConfirm(true);
            }}
          >
            <HighlightOff /> Reject Selected
          </button>
          <button
            className="rv-btn rv-btn-add"
            onClick={() => {
              setEditRow(null);
              setFormValues(EMPTY_FORM);
              setSaveError(null);
              setDrawerMode("add");
            }}
          >
            <AddCircleOutline /> Add Relationship
          </button>
        </div>
      </div>

      {acceptError && <p className="rv-toolbar-error">{acceptError}</p>}

      {/* Table */}
      <div className="rv-table-wrapper">
        <div className="rv-table-scroll">
          <table className="rv-table">
            <thead>
              <tr>
                <th className="rv-col-check">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                  />
                </th>
                <th>SOURCE EQUIPMENT</th>
                <th>SOURCE TYPE</th>
                <th>RELATIONSHIP</th>
                <th>DESCRIPTION</th>
                <th>DESTINATION TYPE</th>
                <th>DESTINATION EQUIPMENT</th>
                <th>STATUS</th>
                <th>SOURCE</th>
                <th className="rv-col-actions">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={10} className="rv-table-empty">
                    Loading relationships...
                  </td>
                </tr>
              )}
              {!isLoading && error && (
                <tr>
                  <td colSpan={10} className="rv-table-empty rv-table-error">
                    {error}
                  </td>
                </tr>
              )}
              {!isLoading && !error && filteredRows.length === 0 && (
                <tr>
                  <td colSpan={10} className="rv-table-empty">
                    No relationships found.
                  </td>
                </tr>
              )}
              {!isLoading &&
                !error &&
                filteredRows.map((row) => {
                  const isSelected = selectedIds.has(row.id);
                  return (
                    <tr
                      key={row.id}
                      className={`${isSelected ? "rv-row-selected" : ""} ${
                        row.id === highlightId ? "rv-row-highlight" : ""
                      }`.trim()}
                    >
                      <td className="rv-col-check">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRow(row.id)}
                        />
                      </td>
                      <td className="rv-equipment">{row.sourceEquipment}</td>
                      <td>{row.sourceType}</td>
                      <td>
                        <span
                          className={`rv-rel-chip rv-rel-${row.relationshipTone}`}
                        >
                          {row.relationship}
                        </span>
                      </td>
                      <td className="rv-description">{row.description}</td>
                      <td>{row.destinationType}</td>
                      <td className="rv-equipment">
                        {row.destinationEquipment}
                      </td>
                      <td>
                        <span
                          className={`rv-status rv-status-${row.status.toLowerCase()}`}
                        >
                          {row.status.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`rv-source rv-source-${row.source
                            .toLowerCase()
                            .replace(/[^a-z]/g, "")}`}
                        >
                          {row.source}
                        </span>
                      </td>
                      <td className="rv-col-actions">
                        <button
                          className="rv-icon-btn"
                          title="Edit"
                          onClick={() => {
                            setEditRow(row);
                            setFormValues({
                              sourceAsset: row.sourceEquipment,
                              sourceAssetType: row.sourceType,
                              connectionType: row.relationship,
                              lineStreamDescription: row.description,
                              destinationAsset: row.destinationEquipment,
                              destinationAssetType: row.destinationType
                            });
                            setSaveError(null);
                            setDrawerMode("edit");
                          }}
                        >
                          <EditOutlined />
                        </button>
                        <button
                          className="rv-icon-btn"
                          title="Delete"
                          onClick={() => {
                            setDeleteRow(row);
                            setDeleteError(null);
                          }}
                        >
                          <DeleteOutlined />
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="rv-pagination">
        <div className="rv-page-size">
          Show 10 <KeyboardArrowDown />
        </div>
        <div className="rv-page-nav">
          <button className="rv-page-btn" disabled>
            <KeyboardDoubleArrowLeft />
          </button>
          <button className="rv-page-btn" disabled>
            <ChevronLeft />
          </button>
          <span className="rv-page-info">1 / 10</span>
          <button className="rv-page-btn">
            <ChevronRight />
          </button>
          <button className="rv-page-btn">
            <KeyboardDoubleArrowRight />
          </button>
        </div>
      </div>

      {/* Page actions */}
      <div className="rv-page-actions">
        <button
          className="rv-page-action rv-action-draft"
          onClick={handlePreviewGraph}
        >
          Preview
        </button>
        <button
          className="rv-page-action rv-action-review"
          onClick={handleSubmitReview}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Submit Review"}
        </button>
        <button
          className="rv-page-action rv-action-publish"
          onClick={handleSubmitReview}
          disabled={isSubmitting}
        >
          Skip and Publish
        </button>
      </div>
      {submitError && <p className="rv-toolbar-error">{submitError}</p>}

      {/* Add / Edit Relationship Drawer */}
      {drawerMode && (
        <div className="rv-drawer-root">
          <div className="rv-drawer-overlay" onClick={closeDrawer} />
          <aside className="rv-drawer">
            <div className="rv-drawer-header">
              <h2 className="rv-drawer-title">
                {drawerMode === "add"
                  ? "Add Relationship"
                  : "Edit Relationship"}
              </h2>
              <button className="rv-drawer-close" onClick={closeDrawer}>
                <Close />
              </button>
            </div>

            <div className="rv-drawer-body">
              <p className="rv-drawer-subtitle">
                {drawerMode === "add"
                  ? "Add a new relationship that was not dedected by AI extraction"
                  : "Edit your relationship which you feel AI as deducted it wrong"}
              </p>

              <div className="rv-field">
                <label>
                  Source Equipment <InfoOutlined className="rv-field-info" />
                </label>
                <input
                  type="text"
                  placeholder="Enter Source Equipment"
                  value={formValues.sourceAsset}
                  onChange={(e) =>
                    handleFormChange("sourceAsset", e.target.value)
                  }
                />
              </div>

              <div className="rv-field">
                <label>
                  Source Type <InfoOutlined className="rv-field-info" />
                </label>
                <input
                  type="text"
                  placeholder="Enter Source Type"
                  value={formValues.sourceAssetType}
                  onChange={(e) =>
                    handleFormChange("sourceAssetType", e.target.value)
                  }
                />
              </div>

              <div className="rv-field">
                <label>
                  Relationship <InfoOutlined className="rv-field-info" />
                </label>
                <input
                  type="text"
                  placeholder="Enter Relationship"
                  value={formValues.connectionType}
                  onChange={(e) =>
                    handleFormChange("connectionType", e.target.value)
                  }
                />
              </div>

              <div className="rv-field">
                <label>
                  Description <InfoOutlined className="rv-field-info" />
                </label>
                <textarea
                  rows={3}
                  placeholder="Enter Description"
                  value={formValues.lineStreamDescription}
                  onChange={(e) =>
                    handleFormChange("lineStreamDescription", e.target.value)
                  }
                />
              </div>

              <div className="rv-field">
                <label>
                  Destination Type <InfoOutlined className="rv-field-info" />
                </label>
                <input
                  type="text"
                  placeholder="Enter Destination Type"
                  value={formValues.destinationAssetType}
                  onChange={(e) =>
                    handleFormChange("destinationAssetType", e.target.value)
                  }
                />
              </div>

              <div className="rv-field">
                <label>
                  Destination Equipment{" "}
                  <InfoOutlined className="rv-field-info" />
                </label>
                <input
                  type="text"
                  placeholder="Enter Destination Equipment"
                  value={formValues.destinationAsset}
                  onChange={(e) =>
                    handleFormChange("destinationAsset", e.target.value)
                  }
                />
              </div>

              {saveError && <p className="rv-drawer-error">{saveError}</p>}

              <div className="rv-drawer-note">
                <InfoOutlined className="rv-drawer-note-icon" />
                <span>
                  Changes will be tracked and require review before publishing.
                </span>
              </div>
            </div>

            <div className="rv-drawer-footer">
              <button
                className="rv-drawer-btn rv-drawer-cancel"
                onClick={closeDrawer}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                className="rv-drawer-btn rv-drawer-save"
                onClick={
                  drawerMode === "add"
                    ? handleAddRelationship
                    : handleUpdateRelationship
                }
                disabled={isSaving}
              >
                {drawerMode === "add"
                  ? isSaving
                    ? "Adding..."
                    : "Add Relationship"
                  : isSaving
                  ? "Saving..."
                  : "Save Changes"}
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteRow && (
        <div className="rv-modal-root">
          <div
            className="rv-modal-overlay"
            onClick={() => !isDeleting && setDeleteRow(null)}
          />
          <div className="rv-modal rv-modal-confirm">
            <h2 className="rv-modal-title">Delete Relationship</h2>
            <p className="rv-modal-desc">
              Are you sure you want to delete the relationship between{" "}
              <strong>{deleteRow.sourceEquipment}</strong> and{" "}
              <strong>{deleteRow.destinationEquipment}</strong>? This action
              cannot be undone.
            </p>

            {deleteError && <p className="rv-drawer-error">{deleteError}</p>}

            <div className="rv-modal-footer">
              <button
                className="rv-modal-btn rv-modal-cancel"
                onClick={() => setDeleteRow(null)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="rv-modal-btn rv-modal-delete"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Selected Confirmation Modal */}
      {showRejectConfirm && (
        <div className="rv-modal-root">
          <div
            className="rv-modal-overlay"
            onClick={() => !isRejecting && setShowRejectConfirm(false)}
          />
          <div className="rv-modal rv-modal-confirm">
            <h2 className="rv-modal-title">Reject Selected Relationships</h2>
            <p className="rv-modal-desc">
              Are you sure you want to reject{" "}
              <strong>{selectedIds.size}</strong> selected relationship
              {selectedIds.size === 1 ? "" : "s"}? This action cannot be undone.
            </p>

            {rejectError && <p className="rv-drawer-error">{rejectError}</p>}

            <div className="rv-modal-footer">
              <button
                className="rv-modal-btn rv-modal-cancel"
                onClick={() => setShowRejectConfirm(false)}
                disabled={isRejecting}
              >
                Cancel
              </button>
              <button
                className="rv-modal-btn rv-modal-delete"
                onClick={handleRejectSelected}
                disabled={isRejecting}
              >
                {isRejecting ? "Rejecting..." : "Reject Selected"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Completed Modal */}
      {showReviewModal && (
        <div className="rv-modal-root">
          <div
            className="rv-modal-overlay"
            onClick={() => setShowReviewModal(false)}
          />
          <div className="rv-modal">
            <div className="rv-modal-check">
              <Check />
            </div>
            <h2 className="rv-modal-title">Review Completed</h2>
            <p className="rv-modal-desc">
              The engineering relationship validation has been completed
              successfully. All reviewed relationships have been reconciled
              against the uploaded engineering drawing and are now ready to
              generate the Enterprise Knowledge Graph.
            </p>

            <div className="rv-modal-kpi">
              <div className="rv-kpi">
                <span className="rv-kpi-label">REVIEWED</span>
                <span className="rv-kpi-value">
                  {reviewSummary?.reviewed ?? 0}
                </span>
              </div>
              <div className="rv-kpi">
                <span className="rv-kpi-label">ACCEPTED</span>
                <span className="rv-kpi-value rv-kpi-accepted">
                  {reviewSummary?.accepted ?? 0}
                </span>
              </div>
              <div className="rv-kpi">
                <span className="rv-kpi-label">MODIFIED</span>
                <span className="rv-kpi-value rv-kpi-modified">
                  {reviewSummary?.modified ?? 0}
                </span>
              </div>
              <div className="rv-kpi">
                <span className="rv-kpi-label">DELETED</span>
                <span className="rv-kpi-value rv-kpi-deleted">
                  {reviewSummary?.deleted ?? 0}
                </span>
              </div>
            </div>

            <div className="rv-modal-next">
              <span className="rv-modal-next-title">WHAT HAPPENS NEXT</span>
              <ul>
                <li>
                  <CheckCircle className="rv-modal-next-icon" />
                  Accepted relationships will be published to the Enterprise
                  Knowledge Graph.
                </li>
                <li>
                  <CheckCircle className="rv-modal-next-icon" />
                  Modified relationships will overwrite previous extracted
                  values.
                </li>
                <li>
                  <CheckCircle className="rv-modal-next-icon" />
                  Deleted relationships will be excluded from graph generation.
                </li>
              </ul>
            </div>

            <div className="rv-modal-important">
              <InfoOutlined className="rv-modal-important-icon" />
              <div>
                <span className="rv-modal-important-title">Important</span>
                <p>
                  Graph regeneration will update downstream Engineering
                  Intelligence, Search, Document Intelligence and Management of
                  Change workflows. This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="rv-modal-footer">
              <button
                className="rv-modal-btn rv-modal-cancel"
                onClick={() => setShowReviewModal(false)}
                disabled={isGeneratingGraph}
              >
                Cancel
              </button>
              <button
                className="rv-modal-btn rv-modal-generate"
                onClick={handleGenerateKnowledgeGraph}
                disabled={isGeneratingGraph}
              >
                {isGeneratingGraph
                  ? "Generating..."
                  : "Generate Knowledge Graph"}
              </button>
            </div>
            {generateGraphError && (
              <p className="rv-modal-error">{generateGraphError}</p>
            )}
          </div>
        </div>
      )}

      {/* Knowledge Graph Preview Modal */}
      {showPreviewModal && (
        <div className="rv-modal-root">
          <div
            className="rv-modal-overlay"
            onClick={() => setShowPreviewModal(false)}
          />
          <div className="rv-modal rv-modal-preview">
            <div className="rv-modal-preview-header">
              <div>
                <h2 className="rv-modal-title">Knowledge Graph Preview</h2>
                <p className="rv-modal-preview-subtitle">
                  Preview of the knowledge graph based on your current
                  relationship edits, before it is generated.
                </p>
              </div>
              <button
                className="rv-drawer-close"
                onClick={() => setShowPreviewModal(false)}
              >
                <Close />
              </button>
            </div>

            <div className="rv-modal-preview-body">
              {isLoadingPreview ? (
                <div className="rv-modal-preview-state">
                  Loading knowledge graph preview...
                </div>
              ) : previewError ? (
                <div className="rv-modal-preview-state rv-modal-preview-error">
                  {previewError}
                </div>
              ) : previewNodes.length === 0 ? (
                <div className="rv-modal-preview-state">
                  No relationships available to preview.
                </div>
              ) : (
                <div className="rv-modal-preview-graph">
                  <GraphCanvas
                    nodes={previewNodes}
                    edges={previewEdges}
                    selectedNodeId={previewSelectedNodeId}
                    onNodeSelect={setPreviewSelectedNodeId}
                  />
                </div>
              )}
            </div>

            <div className="rv-modal-footer">
              <button
                className="rv-modal-btn rv-modal-cancel"
                onClick={() => setShowPreviewModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RelationshipValidation;
